import { Close } from '@carbon/icons-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { CdIconButton } from '../../atoms/CdIconButton';
import { CdHeading } from '../../atoms/CdTypography';
import { cx } from '../../utils';
import styles from './CdSidePanel.module.scss';

export interface CdSidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Hide the visible title (still used as the accessible name). */
  hideTitle?: boolean;
  /** Render without the header bar – the content provides its own close control. */
  bare?: boolean;
  /** Extra controls in the header next to the close button (e.g. "Clear filters"). */
  headerActions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'left';
  /** `nav` = navy surface for the mobile navigation drawer. */
  tone?: 'light' | 'nav';
  closeLabel?: string;
  className?: string;
}

/**
 * Modal sheet built on the native <dialog> element: focus trap, Esc-to-close, inert background and
 * top-layer stacking come from the platform. Full-screen below `md`, a 20rem sheet above.
 */
export const CdSidePanel = ({
  open,
  onClose,
  title,
  hideTitle = false,
  bare = false,
  headerActions,
  children,
  footer,
  side = 'right',
  tone = 'light',
  closeLabel = 'Close',
  className,
}: CdSidePanelProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    // Esc is handled natively through the dialog 'cancel' event; the click handler only adds
    // backdrop-click-to-close for pointer users, so no extra keyboard listener is needed.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      className={cx(styles.panel, styles[side], styles[tone], className)}
      aria-labelledby={bare ? undefined : titleId}
      aria-label={bare ? title : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Clicking the backdrop (the dialog element itself, outside the surface) closes the panel.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.surface}>
        {!bare && (
          <header className={cx(styles.header, hideTitle && styles.headerBare)}>
            <CdHeading level={2} size="sm" id={titleId} className={cx(hideTitle && styles.srOnly)}>
              {title}
            </CdHeading>
            <div className={styles.headerActions}>
              {headerActions}
              <CdIconButton
                label={closeLabel}
                icon={Close}
                onClick={onClose}
                onDark={tone === 'nav'}
                align="bottom-end"
              />
            </div>
          </header>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </dialog>
  );
};
